import { Box, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';

type AssociatedRecord = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  external_id?: string;
  phone_number1: string;
  phone_number2?: string;
  phone_number3?: string;
  dob?: string;
  created_at?: string;
  updated_at?: string;
};

export default function AssociatedRecordsPanel({
  associatedRecords,
  formatDate
}: {
  associatedRecords?: { integration_entity_contacts?: AssociatedRecord[] };
  formatDate?: (date: string) => string;
}) {
  const displayDate = (date?: string) => {
    if (!date) return '—';
    return formatDate ? formatDate(date) : date;
  };

  return (
    <Box
      sx={{
        border: '1px solid #ECECEC',
        borderRadius: '4px',
        p: 2,
        mb: 2,
        background: '#fff'
      }}
    >
      <Typography mb={1} fontSize={16} fontWeight={500}>
        Associated Records{' '}
        {associatedRecords?.integration_entity_contacts?.length
          ? `(${associatedRecords.integration_entity_contacts.length})`
          : ''}
      </Typography>
      {associatedRecords?.integration_entity_contacts?.length ? (
        <Box display='flex' flexDirection='column' gap={1}>
          {associatedRecords.integration_entity_contacts.map((record) => (
            <Box
              key={record.id}
              sx={{
                border: '1px solid #ECECEC',
                borderRadius: '8px',
                p: 1.5,
                background: '#fafafa'
              }}
            >
              <Box display='flex' justifyContent='space-between' borderBottom='1px solid #ECECEC' pb={2}>
                <Box>
                  <Typography fontSize={14} fontWeight={500}>
                    {`${record.first_name} ${record.last_name}`}
                  </Typography>
                  <Typography fontWeight={400} fontSize={12} color='text.secondary' lineHeight={1}>
                    {record.email}
                  </Typography>
                </Box>
                <Tooltip
                  title={record.external_id}
                  placement='top'
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#000',
                        color: 'white',
                        boxShadow: 3,
                        fontSize: '0.85rem',
                        borderRadius: 1,
                        p: 1,
                        lineHeight: 1.2
                      }
                    },
                    arrow: { sx: { color: '#000' } }
                  }}
                >
                  <Typography fontWeight={400} fontSize={12} color='text.secondary' maxWidth={250} noWrap>
                    Ext - {record.external_id}
                  </Typography>
                </Tooltip>
              </Box>

              <Grid container spacing={2} mt={0.5}>
                <Grid size={3}>
                  <Typography variant='caption' color='text.secondary'>
                    Phone 1
                  </Typography>
                  <Typography fontWeight={400} fontSize={12}>
                    {record.phone_number1}
                  </Typography>
                </Grid>

                {record.phone_number2 && (
                  <Grid size={3}>
                    <Typography variant='caption' color='text.secondary'>
                      Phone 2
                    </Typography>
                    <Typography fontWeight={400} fontSize={12}>
                      {record.phone_number2}
                    </Typography>
                  </Grid>
                )}
                {record.phone_number3 && (
                  <Grid size={3}>
                    <Typography variant='caption' color='text.secondary'>
                      Phone 3
                    </Typography>
                    <Typography fontWeight={400} fontSize={12}>
                      {record.phone_number3}
                    </Typography>
                  </Grid>
                )}

                {record.dob && (
                  <Grid size={3}>
                    <Typography variant='caption' color='text.secondary'>
                      DOB
                    </Typography>
                    <Typography fontWeight={400} fontSize={12}>
                      {record.dob}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={2} mt={1}>
                <Grid size={6}>
                  <Typography variant='caption' color='text.secondary'>
                    Created At
                  </Typography>
                  <Typography fontWeight={400} fontSize={12}>
                    {displayDate(record.created_at)}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant='caption' color='text.secondary'>
                    Updated At
                  </Typography>
                  <Typography fontWeight={400} fontSize={12}>
                    {displayDate(record.updated_at)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      ) : (
        <BuilderEmptyStateView minHeight={150} message='No Record or extractable information found for this call' />
      )}
    </Box>
  );
}
